import { radioEventParams, trackEvent } from "./analytics.ts";
import { rowExtras, setRowExtras } from "../row-extra.ts";
import { createSettingControl, readSettingControl } from "./setting-fields.ts";
import type { ExtraField } from "./setting-fields.ts";
import { requireRuntimeApi } from "./state.ts";
import type { RadioSessionHandle } from "./state.ts";
import type { RuntimeApi } from "../runtime-rpc.ts";
import type { UiContext } from "../types/ui-context.js";
import type { ChannelRow } from "./channel-values.ts";

// The per-channel extras editor: the modal behind the grid's Extra column.
//
// A driver hangs settings off a memory that no CSV column can hold -- Busy
// Channel Lockout, PTT-ID, signalling code, scramble. Desktop CHIRP shows them
// as optional extra columns; here one modal edits one channel's set, because
// the list is per-driver and would otherwise widen the grid by an unpredictable
// number of columns.
//
// The values live on the row itself, under the sidecar key in
// web/js/row-extra.ts, which is the only thing the upload path reads
// (_apply_row_extras in web/python/webchirp_bridge/channel_extra.py). Their
// *schema* -- type, options, bounds -- can only come from the driver, so it is
// fetched per open from the memory the row occupies. Overlaying the row's
// stored values on that schema is what makes a moved or edited channel show its
// own settings rather than the ones sitting in the slot it now occupies.
export function createChannelExtra(ctx: UiContext) {
  const { dom, state, log } = ctx;

  // The row currently being edited, and one entry per rendered field. Both are
  // dropped on close, which is also what makes a late response harmless: it
  // finds a different row (or none) and returns.
  let editedRow: ChannelRow | null = null;
  // The radio session the fields were read from. Their names and options are
  // that driver's; save() refuses once it is no longer the selected radio's.
  let editedSession: RadioSessionHandle | null = null;
  let fieldControls: ReturnType<typeof appendField>[] = [];
  // The grid button the open came from, refocused when the modal closes.
  let triggerElement: HTMLElement | null = null;
  // Bumped on every open so the response to a superseded open cannot render
  // over the one the user is looking at.
  let openToken = 0;

  const FIELD_ID_PREFIX = "channel-extra-field-";

  function isModalOpen() {
    return !dom.channelExtraModalEl.classList.contains("hidden");
  }

  function setModalOpen(open: boolean): void {
    dom.channelExtraModalEl.classList.toggle("hidden", !open);
    if (!open) {
      editedRow = null;
      editedSession = null;
      fieldControls = [];
      // Hand the keyboard back to where it came from; without this it is left
      // inside a hidden dialog and the next Tab starts from the top of the page.
      triggerElement?.focus?.();
      triggerElement = null;
    }
  }

  function closeModal() {
    setModalOpen(false);
  }

  function setMessage(text: string): void {
    const message = String(text || "");
    dom.channelExtraMessageEl.textContent = message;
    dom.channelExtraMessageEl.hidden = message === "";
  }

  // One label cell and one control cell per field, filling the modal's
  // two-column grid. Returns the entry the save path reads the field back
  // through.
  function appendField(field: ExtraField, current: unknown) {
    const controlId = `${FIELD_ID_PREFIX}${field.name}`;
    const labelCell = document.createElement("div");
    labelCell.className = "channel-extra-label";
    const label = document.createElement("label");
    label.htmlFor = controlId;
    label.textContent = field.label || field.name;
    labelCell.appendChild(label);
    if (field.doc) {
      const doc = document.createElement("div");
      doc.className = "channel-extra-doc";
      doc.textContent = field.doc;
      labelCell.appendChild(doc);
    }

    const controlCell = document.createElement("div");
    controlCell.className = "channel-extra-control";
    if (field.mutable === false) {
      controlCell.classList.add("is-immutable");
    }
    const control = createSettingControl(field, current);
    control.id = controlId;
    control.name = field.name;
    controlCell.appendChild(control);
    const errorEl = document.createElement("div");
    errorEl.className = "channel-extra-error";
    errorEl.hidden = true;
    controlCell.appendChild(errorEl);

    dom.channelExtraGridEl.appendChild(labelCell);
    dom.channelExtraGridEl.appendChild(controlCell);
    return {
      field,
      control,
      // What the field opened on, so the save can tell an edit from a value
      // that was merely on display. See save().
      initial: current,
      setError(text: string) {
        errorEl.textContent = String(text || "");
        errorEl.hidden = !text;
        controlCell.classList.toggle("is-invalid", Boolean(text));
      },
    };
  }

  function renderFields(fields: readonly ExtraField[], stored: Record<string, unknown>): void {
    dom.channelExtraGridEl.innerHTML = "";
    fieldControls = fields.map((field) => {
      // The row's own value wins over the one read from the slot: it is what
      // this channel carries, wherever it has been moved to since.
      const current = Object.hasOwn(stored, field.name) ? stored[field.name] : field.current;
      return appendField(field, current);
    });
    fieldControls[0]?.control.focus?.();
  }

  // A channel that carries nothing of its own has no values to show but the
  // ones sitting in the memory it points at, and those belong to whatever was
  // there before -- a channel this one replaced, or the image's own occupant.
  // Saying so is what keeps the form from reading as "your channel's settings",
  // and pairs with the save writing back only what was actually changed.
  function startingValuesNote(stored: Record<string, unknown>, location: unknown): string {
    if (Object.keys(stored).length > 0) {
      return "";
    }
    return `This channel has no settings of its own yet, so the values shown are `
      + `what memory ${location || "this slot"} currently holds. Only what you `
      + `change is stored on the channel.`;
  }

  // Open the editor for one grid row. The modal opens before the runtime is
  // asked anything, so a slow first call (this can be the one that boots
  // Pyodide) shows a dialog that is loading rather than a click that did
  // nothing.
  async function openForRow(rowIdx: number, trigger: HTMLElement | null = null) {
    const row = state.currentRows[rowIdx];
    if (!row) {
      return;
    }
    // Where focus goes back to on every close path. The grid hands its button
    // over rather than this reading document.activeElement, because a click
    // does not focus a button on every platform (Safari does not).
    triggerElement = trigger;
    const radio = state.selectedRadio;
    const token = openToken + 1;
    openToken = token;
    editedRow = row;
    editedSession = ctx.session.current();
    const location = String(row.Location ?? "").trim();
    dom.channelExtraTitleEl.textContent = location
      ? `Extra settings for channel ${location}`
      : "Extra settings";
    dom.channelExtraGridEl.innerHTML = "";
    fieldControls = [];
    dom.channelExtraSaveEl.disabled = true;
    setMessage("Reading this channel's extra settings from the driver...");
    setModalOpen(true);
    // Immediately, not when the fields arrive: the read can be the call that
    // boots Pyodide, and until focus is inside the dialog the keyboard is still
    // on the button behind the overlay -- permanently so when the read fails or
    // the driver has nothing to offer, since no field is ever rendered.
    dom.channelExtraCancelEl.focus?.();

    if (!radio) {
      setMessage("Select a radio to edit driver-specific channel settings.");
      return;
    }

    let payload: Awaited<ReturnType<RuntimeApi["getChannelExtra"]>> | null = null;
    try {
      payload = await requireRuntimeApi(state).getChannelExtra({
        sessionId: await ctx.session.currentId(),
        location,
      });
    } catch (error) {
      if (token !== openToken || editedRow !== row) {
        return;
      }
      trackEvent("channel_extra_opened", { ...radioEventParams(radio), outcome: "failed" });
      setMessage("Extra settings could not be read for this channel.");
      log.reportActionError("Channel extra settings", error);
      return;
    }
    // A response for a row the user has since closed or navigated away from has
    // nowhere to render.
    if (token !== openToken || editedRow !== row) {
      return;
    }
    if (!payload?.available) {
      trackEvent("channel_extra_opened", { ...radioEventParams(radio), outcome: "unavailable" });
      setMessage(payload?.message || "This channel has no extra settings.");
      return;
    }
    const stored = rowExtras(row) || {};
    setMessage(startingValuesNote(stored, location));
    renderFields(payload.fields || [], stored);
    dom.channelExtraSaveEl.disabled = false;
    trackEvent("channel_extra_opened", { ...radioEventParams(radio), outcome: "ok" });
  }

  // Write the edited values onto the row.
  //
  // Only the fields the user actually changed, and never an immutable one. The
  // schema was read from the memory this row's Location points at, so a field
  // left alone is showing that memory's value -- which for a channel created,
  // pasted or imported over an occupied slot belongs to the *previous
  // occupant*. Storing those would hand a replacement channel the settings of
  // the one it replaced, which is the exact failure the row sidecar exists to
  // prevent (see web/python/webchirp_bridge/channel_extra.py). A field nobody
  // touched simply stays out of the sidecar, and the driver's own defaults
  // apply to it at upload.
  function save() {
    const row = editedRow;
    if (!row) {
      return;
    }
    // A download or an image load replaces state.currentRows wholesale without
    // closing this modal, which would leave the save mutating a row that is no
    // longer in the editor -- reported as success, absent from what is later
    // uploaded.
    if (!state.currentRows.includes(row)) {
      setModalOpen(false);
      log.setStatus("The channel list changed while the extra settings were open; nothing was saved.");
      return;
    }
    // Choosing another radio keeps the rows but not the driver these fields
    // came from: their names are the old driver's, and the upload path skips a
    // stored name the new driver lacks (_apply_row_extras_to_memory in
    // web/python/webchirp_bridge/channel_extra.py), so the value would be
    // reported saved here and silently dropped there.
    if (!ctx.session.isCurrent(editedSession)) {
      setModalOpen(false);
      log.setStatus("The selected radio changed while the extra settings were open; nothing was saved.");
      return;
    }
    const values: Record<string, unknown> = {};
    let invalid = 0;
    for (const entry of fieldControls) {
      const { value, error } = readSettingControl(entry.field, entry.control);
      entry.setError(error);
      if (error) {
        invalid += 1;
        continue;
      }
      if (entry.field.mutable === false) {
        continue;
      }
      // Compared as text so a number input handing back 3 for an initial "3"
      // still reads as untouched; every value here is a primitive.
      if (String(value) === String(entry.initial ?? "")) {
        continue;
      }
      values[entry.field.name] = value;
    }
    if (invalid > 0) {
      setMessage(`Fix ${invalid} highlighted value${invalid === 1 ? "" : "s"} before saving.`);
      return;
    }
    const changedCount = Object.keys(values).length;
    const location = String(row.Location ?? "").trim();
    if (changedCount === 0) {
      // Nothing to record: a row must not pick up a sidecar it does not need,
      // which is what tells the upload path to leave the driver's own values
      // alone for this channel.
      setModalOpen(false);
      log.setStatus(`No extra settings were changed for channel ${location || "?"}.`);
      return;
    }
    setRowExtras(row, values);
    // The driver judges a channel with its extras, so a check in flight for
    // this row answers for one that no longer exists.
    ctx.table.rowsRewritten([row]);
    setModalOpen(false);
    // No re-render: the grid shows the same button whatever a row carries, and
    // the Extra column is already up (nothing else could have opened this).
    trackEvent("channel_extra_saved", radioEventParams(state.selectedRadio));
    log.setStatus(
      `Extra settings updated for channel ${location || "?"}; they are written on `
      + "upload to the radio or image export. CSV export does not carry them.",
    );
  }

  function bindEvents() {
    dom.channelExtraCancelEl.addEventListener("click", () => {
      setModalOpen(false);
    });
    dom.channelExtraModalEl.addEventListener("click", (event) => {
      if (event.target === dom.channelExtraModalEl) {
        setModalOpen(false);
      }
    });
    dom.channelExtraFormEl.addEventListener("submit", (event) => {
      event.preventDefault();
      save();
    });
  }

  return { bindEvents, isModalOpen, closeModal, openForRow };
}
